import { Link } from "react-router-dom";
import { Phone, ArrowDown, CalendarCheck, Instagram, Facebook } from "lucide-react";
import { businessConfig } from "@/config";

const POLAROIDS = [
  { id: 1, rotate: "-6deg", top: "2%",  left: "0%",  width: "40%" },
  { id: 2, rotate: "4deg",  top: "1%",  left: "28%", width: "36%" },
  { id: 3, rotate: "-2deg", top: "0%",  left: "55%", width: "42%" },
  { id: 4, rotate: "5deg",  top: "52%", left: "2%",  width: "38%" },
  { id: 5, rotate: "-4deg", top: "50%", left: "30%", width: "42%" },
  { id: 6, rotate: "3deg",  top: "51%", left: "58%", width: "40%" },
];

function PolaroidFrame({ p }) {
  const src = businessConfig.media?.[`polaroid${p.id}`];
  return (
    <div
      style={{
        position: "absolute",
        top: p.top,
        left: p.left,
        width: p.width,
        transform: `rotate(${p.rotate})`,
        background: "#fff",
        borderRadius: "4px",
        padding: "6px 6px 18px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        cursor: "pointer",
        transition: "transform 300ms ease, box-shadow 300ms ease",
        zIndex: 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "rotate(0deg) scale(1.08)";
        e.currentTarget.style.zIndex = "10";
        e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,0.6)";
        const media = e.currentTarget.querySelector("[data-media]");
        if (media) media.style.aspectRatio = "9 / 16";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = `rotate(${p.rotate})`;
        e.currentTarget.style.zIndex = "1";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.4)";
        const media = e.currentTarget.querySelector("[data-media]");
        if (media) media.style.aspectRatio = "1 / 1";
      }}
    >
      {/* Gold pin */}
      <div
        style={{
          position: "absolute",
          top: -8,
          left: "50%",
          transform: "translateX(-50%)",
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "radial-gradient(circle at 35% 35%, #f5d07a, var(--pp-gold))",
          boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
          zIndex: 2,
        }}
      />
      {/* Media */}
      <div
        data-media=""
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          borderRadius: "2px",
          overflow: "hidden",
          background: "linear-gradient(135deg, var(--pp-indigo) 0%, var(--pp-rescue) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "aspect-ratio 300ms ease",
        }}
      >
        {src ? (
          src.endsWith(".mp4") || src.endsWith(".webm") ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            >
              <source src={src} type="video/mp4" />
            </video>
          ) : (
            <img
              src={src}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )
        ) : (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
            }}
          />
        )}
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { heroVideoDesktop, heroPoster } = businessConfig.media || {};

  return (
    <section
      className="hero"
      id="top"
      data-testid="hero-section"
      style={{
        minHeight: "600px",
        height: "calc(100vh - 64px)",
        maxHeight: "800px",
      }}
    >
      {/* Full bleed video background */}
      <div className="hero__media" aria-hidden>
        {heroVideoDesktop && (
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster={heroPoster || undefined}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          >
            <source src={heroVideoDesktop} type="video/mp4" />
          </video>
        )}
      </div>
      <div className="hero__overlay" aria-hidden />

      {/* Two column layout */}
      <div
        className="hero-two-col"
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "16px 48px 24px 48px",
          display: "grid",
          gridTemplateColumns: "1fr 1.1fr",
          gap: "48px",
          alignItems: "stretch",
          height: "100%",
        }}
      >
        {/* LEFT — frosted glass card */}
        <div
          className="hero-glass-card"
          style={{
            background: "rgba(42, 37, 64, 0.55)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "16px",
            padding: "24px 40px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "14px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <span className="overline" style={{ color: "var(--pp-gold)" }}>
            MOOSE JAW · SK
          </span>
          <h1
            style={{
              fontFamily: "'Lora', serif",
              fontWeight: 500,
              fontSize: "clamp(32px, 3.5vw, 52px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              color: "#fff",
              margin: 0,
            }}
          >
            It all began with{" "}
            <em style={{ fontStyle: "italic", color: "#B5BAFF" }}>Pup</em>;
          </h1>
          <p
            style={{
              fontFamily: "'Lora', serif",
              fontSize: "clamp(15px, 1.6vw, 20px)",
              color: "rgba(255,255,255,0.92)",
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            where every animal is treated as their own.
          </p>
          <span
            style={{ display: "block", width: 40, height: 2, background: "var(--pp-gold)" }}
          />
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.8)",
              margin: 0,
              maxWidth: 420,
            }}
          >
            Master-certified grooming, daycare &amp; boarding for the dogs and
            cats of Moose Jaw &amp; area.
          </p>

          {/* CTAs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <a
              href="tel:3066910001"
              className="hero__btn-call"
              data-testid="hero-call-btn"
            >
              <Phone className="w-4 h-4" />
              Call now · 306-691-0001
            </a>
            <div
            style={{
  display: "flex",
  gap: 8,
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
}}
            >
              <Link to="/book" className="pp-btn-confirm" data-testid="hero-book-btn">
                <CalendarCheck className="w-4 h-4" />
                Book appointment
              </Link>
              <a href="#services" className="hero__btn-outline" data-testid="hero-services-btn">
                See services <ArrowDown className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* RIGHT — cork board with polaroids */}
        <div
          className="hero-board"
          style={{
            background: "rgba(30, 20, 40, 0.72)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "16px",
            padding: "16px 16px 12px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            height: "100%",
            overflow: "visible"
          }}
        >
          {/* Board header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "'Lora', serif",
              fontStyle: "italic",
              fontSize: 15,
              color: "rgba(255,255,255,0.75)",
              flexShrink: 0,
            }}
          >
            <Instagram size={15} style={{ color: "var(--pp-gold)" }} />
            <span>Happy pets</span>
          </div>

          {/* Polaroid cluster */}
          <div
            style={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              overflow: "Visible",
            }}
          >
            {POLAROIDS.map((p) => (
              <PolaroidFrame key={p.id} p={p} />
            ))}
          </div>

          {/* Social follow pills */}
          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
            <a
              href="https://www.instagram.com/pupsparlour.grooming"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "8px 12px",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "999px",
                color: "#fff",
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "none",
                transition: "background 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
            >
              <Instagram size={13} />
              Instagram
            </a>
            <a
              href="https://www.facebook.com/pawsnplaygroomcatlodgerescue"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "8px 12px",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "999px",
                color: "#fff",
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "none",
                transition: "background 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
            >
              <Facebook size={13} />
              Facebook
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
